
import React, { useEffect, useState } from 'react'
import OpeningMessage, { OpeningMessageData } from './OpeningMessage'
import AddResponse from './AddResponse'
import AskForFollow from './AskForFollow'
import TextButton from './TextButton'
import CardEditor from '../CardEditor'
import { CardData } from '@Types/common'
import UserInput from './UserInput'
import SendToMeragiAPI from './SendToMeragiAPI'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@Components/shadcn/ui/accordion'
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react'

`**response data structure**
Always an array of dict, with first dict adding the opening message, and the rest adding the components which 
response = {response_data: [], opening_message: {}}
`

interface ResponseItem {
    id: string;
    type: string;
    data: any;
}

interface ResponseDataState {
    opening_message: {
        enabled: boolean;
    };
    data: ResponseItem[];
}

function ResponseStepV2({ data, onUpdate, automationId, validationErrors = [] }: { data: ResponseDataState, onUpdate: (data: any) => void, automationId: string | number | undefined, validationErrors?: string[] }) {

    const [openAccordionItem, setOpenAccordionItem] = useState<string | undefined>(undefined)
    const [isAnimating, setIsAnimating] = useState(false)


    // Update parent only when we make internal changes

    const updateComponentData = (componentData: any, index: number) => {
        const ResponseDataCopy = { ...data }

        console.log(ResponseDataCopy)
        ResponseDataCopy.data = ResponseDataCopy.data.map((item: any, idx: number) => {
            if (index === idx) {
                return componentData
            }
            return item
        })
        onUpdate(ResponseDataCopy)
    }

    const addComponent = (component: ResponseItem) => {
        const ResponseDataCopy = { ...data, data: [...data.data] }
        ResponseDataCopy.data.push(component)
        onUpdate(ResponseDataCopy)
        setOpenAccordionItem(`item_${ResponseDataCopy.data.length - 1}`)
    }

    const handleAccordionChange = (value: string | undefined) => {
        setOpenAccordionItem(value)
    }

    const moveComponent = (index: number, direction: 'up' | 'down') => {
        if (isAnimating) return // Prevent multiple animations

        const newData = [...data.data]
        const targetIndex = direction === 'up' ? index - 1 : index + 1

        if (targetIndex >= 0 && targetIndex < newData.length) {
            // Swap the items
            const temp = newData[index]
            newData[index] = newData[targetIndex]
            newData[targetIndex] = temp

            const updatedData = { ...data, data: newData }
            onUpdate(updatedData)
        }
    }

    const deleteComponent = (index: number) => {
        const newData = [...data.data]
        newData.splice(index, 1)
        const updatedData = { ...data, data: newData }

        onUpdate(updatedData)

        // Close accordion if the deleted item was open
        if (openAccordionItem === `item_${index}`) {
            setOpenAccordionItem(undefined)
        }
    }

    const UpdateOpeningMessage = (opening_message: OpeningMessageData) => {
        onUpdate({ ...data, opening_message: opening_message })

    }

    const getComponentTitle = (item: any) => {
        switch (item.type) {
            case 'ask_follow':
                return { title: 'Ask For Follow', subtitle: 'Request users to follow your account' }
            case 'text_message':
                return { title: 'Text Message', subtitle: 'Send a simple Text or Button Response' }
            case 'card_message':
                return { title: 'Card Message', subtitle: 'Send a rich card with Image, Texts and Button' }
            case 'user_input':
                return { title: 'Lead Forms', subtitle: 'Request users to input text' }
            case 'send_to_meragi_api':
                return { title: 'Send to Meragi API', subtitle: 'Send user data to Meragi API endpoint' }
            default:
                return { title: 'Component', subtitle: 'Component' }
        }
    }
    return (
        <div className='w-full mx-auto bg-white rounded-[20px] p-4 md:p-6'>
            <h2 className="text-2xl font-bold text-gray-900">Response Flow</h2>

            {/* Validation Errors Display */}
            {validationErrors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Validation Errors</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <ul className="list-disc list-inside space-y-1">
                                    {validationErrors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <OpeningMessage data={data?.opening_message} UpdateOpeningMessage={UpdateOpeningMessage} automationId={automationId} />

            <Accordion
                type="single"
                collapsible={true}
                className="w-full"
                value={openAccordionItem}

                onValueChange={handleAccordionChange}
            >
                {data?.data?.map((item: any, idx: number) => {
                    const allowedTypes = ['ask_follow', 'text_message', 'card_message', 'user_input', 'send_to_meragi_api'];
                    if (!allowedTypes.includes(item?.type)) return null;

                    const renderComponent = () => {
                        if (item?.type === 'ask_follow') {
                            return <AskForFollow index={idx} aff_data={item} update_response_data={(data) => updateComponentData(data, idx)} automationId={automationId} />
                        } else if (item?.type === 'text_message') {
                            return <TextButton index={idx} automationId={automationId} data={item} update_response_data={(data: any) => updateComponentData(data, idx)} />
                        } else if (item?.type === 'card_message') {
                            return <CardEditor index={idx} automationId={automationId} data={item} update_response_data={(data: any) => updateComponentData(data, idx)} />
                        } else if (item?.type === 'user_input') {
                            return <UserInput index={idx} data={item} update_response_data={(data: any) => updateComponentData(data, idx)} />
                        } else if (item?.type === 'send_to_meragi_api') {
                            return <SendToMeragiAPI index={idx} data={item} update_response_data={(data: any) => updateComponentData(data, idx)} />
                        }
                        return null
                    }

                    return (
                        <div key={idx} className="relative">
                            <AccordionItem
                                value={`item_${idx}`}
                                className={`bg-gray-100 border rounded-lg transition-all duration-300 ${isAnimating ? 'transform transition-transform duration-300 ease-in-out' : ''
                                    }`}
                            >
                                <AccordionTrigger className="flex items-center px-4 py-1 hover:no-underline">
                                    {/* Step Number Circle */}
                                    <div className="flex items-center justify-center w-8 h-8 bg-black text-white rounded-full text-sm font-bold mr-3 flex-shrink-0">
                                        {idx + 1}
                                    </div>

                                    <div className="flex flex-col items-center gap-1 mr-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                moveComponent(idx, 'up')
                                            }}
                                            disabled={idx === 0 || isAnimating}
                                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                        >
                                            <ChevronUp className="h-4 w-4 font-bold" />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                moveComponent(idx, 'down')
                                            }}
                                            disabled={idx === data.data.length - 1 || isAnimating}
                                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                        >
                                            <ChevronDown className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <span className="font-medium flex-1 text-left">
                                        <h2 className='text-md font-bold'>{getComponentTitle(item)?.title}</h2>
                                        {/* <p className='text-sm text-gray-500'>{getComponentTitle(item)?.subtitle}</p> */}
                                    </span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            deleteComponent(idx)
                                        }}
                                        className="p-2 hover:bg-red-100 rounded-full text-red-500 hover:text-red-700 transition-colors duration-200 ml-2"
                                        title="Delete component"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </AccordionTrigger>
                                <AccordionContent className="px-4 pb-4">
                                    {/* Hello */}
                                    {renderComponent()}
                                </AccordionContent>
                            </AccordionItem>

                            {/* Dotted Line - Show only if not the last component */}
                            {idx < data.data.length - 1 && (
                                <div className="flex justify-center">
                                    <div className="w-px h-3 border-l-2 border-dotted border-gray-400"></div>
                                </div>
                            )}
                        </div>
                    )
                })}
            </Accordion>
            <AddResponse add_component={addComponent} response_data={data} />

        </div>
    )
}

export default ResponseStepV2